import React, { useState, useRef, useEffect } from "react";
import { CloseIcon } from "./Icons";

function TagMultiSelect({ allTags, selectedTagIds, onTagsChange, onCreateTag }) {
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [createError, setCreateError] = useState(null);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  // Get selected tags as objects
  const selectedTags = allTags.filter((t) => selectedTagIds.includes(t.id));

  // Filter suggestions based on input
  const suggestions = inputValue.trim()
    ? allTags.filter(
        (t) =>
          t.name.toLowerCase().includes(inputValue.toLowerCase()) &&
          !selectedTagIds.includes(t.id)
      )
    : allTags.filter((t) => !selectedTagIds.includes(t.id));

  // Check if input matches an existing tag exactly
  const exactMatch = allTags.find(
    (t) => t.name.toLowerCase() === inputValue.trim().toLowerCase()
  );

  // Show "Create" option if no exact match and input has value
  const showCreateOption =
    inputValue.trim() && !exactMatch && !selectedTags.some(
      (t) => t.name.toLowerCase() === inputValue.trim().toLowerCase()
    );

  const totalOptions = suggestions.length + (showCreateOption ? 1 : 0);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelectTag = (tag) => {
    onTagsChange([...selectedTagIds, tag.id]);
    setInputValue("");
    setHighlightIndex(0);
    inputRef.current?.focus();
  };

  const handleRemoveTag = (tagId) => {
    onTagsChange(selectedTagIds.filter((id) => id !== tagId));
  };

  const handleCreateTag = async () => {
    if (!inputValue.trim()) return;
    try {
      const newTag = await onCreateTag(inputValue.trim());
      onTagsChange([...selectedTagIds, newTag.id]);
      setInputValue("");
      setHighlightIndex(0);
      setCreateError(null);
    } catch (err) {
      console.error("Failed to create tag:", err);
      setCreateError(`Failed to create tag "${inputValue.trim()}"`);
    }
  };

  // Keep the highlighted option visible in the scrollable list
  useEffect(() => {
    if (!showDropdown || !listRef.current) return;
    const el = listRef.current.children[highlightIndex];
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [highlightIndex, showDropdown]);

  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, totalOptions - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (totalOptions === 0) return;
      // Consume Enter so it picks a tag, not a parent modal's submit
      e.stopPropagation();

      if (highlightIndex < suggestions.length) {
        handleSelectTag(suggestions[highlightIndex]);
      } else if (showCreateOption) {
        handleCreateTag();
      }
    } else if (e.key === "Backspace" && !inputValue && selectedTagIds.length > 0) {
      // Remove last tag on backspace if input is empty
      handleRemoveTag(selectedTagIds[selectedTagIds.length - 1]);
    } else if (e.key === "Escape") {
      if (showDropdown) {
        // Consume Escape so it closes the dropdown, not a parent modal
        e.stopPropagation();
        setShowDropdown(false);
      }
    }
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Selected tags and input */}
      <div
        onClick={() => inputRef.current?.focus()}
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "6px",
          padding: "8px",
          background: "var(--bg-tertiary)",
          borderRadius: "var(--radius-md)",
          border: "1px solid var(--border-color)",
          minHeight: "42px",
          cursor: "text",
        }}
      >
        {selectedTags.map((tag) => (
          <span
            key={tag.id}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "4px 8px",
              borderRadius: "var(--radius-sm)",
              background: `${tag.color}30`,
              border: `1px solid ${tag.color}`,
              fontSize: "0.875rem",
            }}
          >
            <span
              style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: tag.color,
              }}
            />
            {tag.name}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveTag(tag.id);
              }}
              style={{
                background: "none",
                border: "none",
                padding: "0",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                opacity: 0.7,
              }}
            >
              <CloseIcon size={12} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowDropdown(true);
            setHighlightIndex(0);
            setCreateError(null);
          }}
          onFocus={() => setShowDropdown(true)}
          onKeyDown={handleKeyDown}
          placeholder={selectedTags.length === 0 ? "Type to add tags..." : ""}
          style={{
            flex: 1,
            minWidth: "100px",
            background: "transparent",
            border: "none",
            outline: "none",
            padding: "4px",
            fontSize: "0.875rem",
          }}
        />
      </div>

      {/* Creation error */}
      {createError && (
        <div
          style={{
            color: "var(--accent-danger)",
            fontSize: "0.75rem",
            marginTop: "4px",
          }}
        >
          {createError}
        </div>
      )}

      {/* Dropdown */}
      {showDropdown && totalOptions > 0 && (
        <div
          ref={listRef}
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: "4px",
            background: "var(--bg-secondary)",
            border: "1px solid var(--border-color)",
            borderRadius: "var(--radius-md)",
            boxShadow: "var(--shadow-lg)",
            maxHeight: "200px",
            overflowY: "auto",
            zIndex: 100,
          }}
        >
          {suggestions.map((tag, index) => (
            <div
              key={tag.id}
              onClick={() => handleSelectTag(tag)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 12px",
                cursor: "pointer",
                background:
                  index === highlightIndex
                    ? "var(--bg-tertiary)"
                    : "transparent",
              }}
            >
              <span
                style={{
                  width: "10px",
                  height: "10px",
                  borderRadius: "50%",
                  background: tag.color,
                }}
              />
              {tag.name}
            </div>
          ))}
          {showCreateOption && (
            <div
              onClick={handleCreateTag}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 12px",
                cursor: "pointer",
                background:
                  highlightIndex === suggestions.length
                    ? "var(--bg-tertiary)"
                    : "transparent",
                borderTop: suggestions.length > 0 ? "1px solid var(--border-color)" : "none",
              }}
            >
              <span style={{ color: "var(--accent-primary)" }}>+</span>
              Create "{inputValue.trim()}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TagMultiSelect;
